import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { consoleCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Link } from '@/ui/Link/Link'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { SegmentedControl } from '@/ui/SegmentedControl/SegmentedControl'
import { resourcesFor } from '../../api/resources'
import { ROUTES } from '../../constants/routes'
import { useThreadsQuery } from '../../hooks/queries'
import { streamKey, useStreamStore } from '../../store/stream'
import type { AgentParticipant } from '../../types/api'
import { ThreadBoard } from './ThreadBoard'
import { TopicAgentHeader } from './TopicAgentHeader'
import { TopicResources } from './TopicResources'
import styles from './TopicView.module.css'

type Tab = 'threads' | 'resources'

interface Props {
  topicId: string
  topicName?: string
  workspaceId?: string
  agents: AgentParticipant[]
  currentUser: string | null
  onSelect: (threadId: string) => void
  /** Active section tab — controlled by the shell so the thread panel's
   * "all topic resources" link can deep-link into the resources tab. */
  tab: Tab
  onTabChange: (tab: Tab) => void
  seenBefore?: number
  topicMountTime: number
}

/**
 * The redesigned topic view (§3): segmented threads/resources tabs over
 * the always-visible agent header, with the status-grouped board or the
 * faceted resources list below. Replaces ThreadListPane's listing when
 * the console-v2 flag is on; the composer stays mounted in the shell.
 */
export function TopicView({
  topicId,
  topicName,
  workspaceId,
  agents,
  currentUser,
  onSelect,
  tab,
  onTabChange,
  seenBefore,
  topicMountTime,
}: Props) {
  const { data: threadsData } = useThreadsQuery(topicId, 'active')
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', 'topic', topicId],
    queryFn: () => resourcesFor({ kind: 'topic', id: topicId }).list(),
    staleTime: 10_000,
  })

  // "While you were away" digest (same definition as the old pane): the
  // active threads updated between the last visit and this mount.
  const activeTurns = useStreamStore((s) => s.activeTurns)
  const awayThreads = useMemo(() => {
    if (seenBefore == null) return []
    return (threadsData?.threads ?? []).filter(
      (t) =>
        t.last_active_at > seenBefore &&
        t.last_active_at <= topicMountTime &&
        !(activeTurns[streamKey(topicId, t.thread_id)]?.length > 0),
    )
  }, [threadsData, seenBefore, topicMountTime, activeTurns, topicId])

  const switchTab: (next: Tab) => void = onTabChange

  const latest =
    awayThreads.length > 0
      ? [...awayThreads].sort((a, b) => b.last_active_at - a.last_active_at)[0]
      : undefined

  return (
    <div className={styles.view}>
      <Inline gap="space-12" className={styles.head}>
        <Text as="h1" variant="title" tone="text" className={styles.title}>
          {topicName ?? topicId}
        </Text>
        <SegmentedControl
          className={styles.tabs}
          label={consoleCopy.topic.views}
          value={tab}
          onChange={switchTab}
          options={[
            {
              value: 'threads',
              label: consoleCopy.topic.threads,
              count: threadsData?.threads.length,
            },
            {
              value: 'resources',
              label: consoleCopy.topic.resources,
              count: resourcesData?.resources.length,
            },
          ]}
        />
      </Inline>

      <TopicAgentHeader topicId={topicId} workspaceId={workspaceId} />

      {tab === 'threads' ? (
        <>
          {latest && (
            <div className={styles.inset}>
              <Alert tone="info">
                <Inline gap="space-8" align="baseline">
                  <span>{consoleCopy.topic.away(awayThreads.length)}</span>
                  <Link
                    href={ROUTES.topicThread(topicId, latest.thread_id)}
                    onClick={(e) => {
                      if (
                        e.metaKey ||
                        e.ctrlKey ||
                        e.shiftKey ||
                        e.altKey ||
                        e.button !== 0
                      )
                        return
                      e.preventDefault()
                      onSelect(latest.thread_id)
                    }}
                  >
                    {consoleCopy.topic.viewLatest}
                  </Link>
                </Inline>
              </Alert>
            </div>
          )}
          <ThreadBoard
            topicId={topicId}
            agents={agents}
            currentUser={currentUser}
            onSelect={onSelect}
          />
        </>
      ) : (
        <TopicResources topicId={topicId} />
      )}
    </div>
  )
}
